import { useState, useEffect } from "react";
import { useTranslation } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Shield, Settings, X } from "lucide-react";

export function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("cookie_consent")) setVisible(true);
  }, []);

  const saveChoice = (choice) => {
    // choice: "all" | "essential" | custom object
    const record = {
      choice: typeof choice === "string" ? choice : "custom",
      essential: true, // always on
      analytics: choice === "all",
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookie_consent", JSON.stringify(record));
    setVisible(false);
    setShowConfig(false);
  };

  if (!visible) return null;

  if (showConfig) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[60] bg-navy-900/95 backdrop-blur-md border-t border-navy-800 px-4 py-4" data-testid="cookie-banner-config">
        <div className="max-w-2xl mx-auto space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white font-sans">{t("cookies.config_title")}</h3>
            <button onClick={() => setShowConfig(false)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-navy-800/50 rounded">
              <div>
                <p className="text-sm text-white font-body">{t("cookies.essential_title")}</p>
                <p className="text-xs text-slate-400 font-body">{t("cookies.essential_desc")}</p>
              </div>
              <span className="text-xs text-emerald-400 font-body">{t("cookies.always_on")}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-navy-800/50 rounded">
              <div>
                <p className="text-sm text-white font-body">{t("cookies.analytics_title")}</p>
                <p className="text-xs text-slate-400 font-body">{t("cookies.analytics_desc")}</p>
              </div>
              <span className="text-xs text-slate-500 font-body">{t("cookies.not_used")}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-navy-800/50 rounded">
              <div>
                <p className="text-sm text-white font-body">{t("cookies.marketing_title")}</p>
                <p className="text-xs text-slate-400 font-body">{t("cookies.marketing_desc")}</p>
              </div>
              <span className="text-xs text-slate-500 font-body">{t("cookies.not_used")}</span>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" className="border-slate-500 text-white hover:bg-navy-800 text-xs" onClick={() => saveChoice("essential")} data-testid="cookie-save-essential">{t("cookies.save_essential")}</Button>
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs" onClick={() => saveChoice("all")} data-testid="cookie-save-all">{t("cookies.accept_all")}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] bg-navy-900/95 backdrop-blur-md border-t border-navy-800 px-4 py-3" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center gap-3">
        <Shield className="w-5 h-5 text-emerald-400 flex-shrink-0 hidden sm:block" />
        <p className="text-sm text-slate-300 font-body flex-1 text-center sm:text-left">{t("cookies.message")}</p>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" className="border-slate-500 text-white hover:bg-navy-800 text-xs" onClick={() => saveChoice("essential")} data-testid="cookie-reject-btn">{t("cookies.reject")}</Button>
          <Button variant="outline" size="sm" className="border-slate-500 text-white hover:bg-navy-800 text-xs" onClick={() => setShowConfig(true)} data-testid="cookie-config-btn">
            <Settings className="w-3 h-3 mr-1" />{t("cookies.configure")}
          </Button>
          <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs" onClick={() => saveChoice("all")} data-testid="cookie-accept-btn">{t("cookies.accept")}</Button>
        </div>
      </div>
    </div>
  );
}
